import { Map } from "immutable";
import { z } from "zod/v4";

import { type Tuple } from "../Tuple";
import {
	type Arrangement,
	ZSet,
	dehydrateArrangement,
	dehydrateZSet,
	hydrateArrangement,
	hydrateZSet,
	rawArrangementSchema,
	rawZSetSchema,
} from "../ZSet";
import { type BilinearOperatorLogic } from "./BilinearOperatorLogic";
import {
	type JsonPathRef,
	type SubqueryRef,
	jsonpathRefSchema,
	subqueryRefSchema,
} from "./schema-util";

export function semijoin<Row, Key>(
	spec: SemijoinSpec
): BilinearOperatorLogic<Row, Key, Row, SemijoinState<Row, Key>> {
	const rowKey = (row: Tuple<Row>): Tuple<Key> => row.pluck(spec.rowKey.path);
	return (
		left: ZSet<Row>,
		right: ZSet<Key>,
		state: SemijoinState<Row, Key> = emptySemijoinState()
	): [ZSet<Row>, SemijoinState<Row, Key>] => {
		let out = ZSet.empty<Row>();

		const newState = {
			type: "semijoin",
			rowSetByKeys: state.rowSetByKeys,
			keySet: state.keySet.clone(),
		} satisfies SemijoinState<Row, Key>;

		// 1. Update key membership counts from right‑hand delta
		for (const ztuple of right) {
			const oldWeight = newState.keySet.weight(ztuple.tuple);
			newState.keySet = newState.keySet.add(ztuple);
			const newWeight = newState.keySet.weight(ztuple.tuple);
			const contentId = ztuple.tuple.contentId();

			const rows = newState.rowSetByKeys.get(contentId) ?? ZSet.empty<Row>();
			if (newWeight > 0) {
				for (const row of rows) {
					out = out.add(row);
				}
			} else if (oldWeight > 0 && newWeight <= 0) {
				for (const row of rows) {
					out = out.add({ ...row, weight: -1 });
				}
			}
		}

		// 2. Filter left‑hand delta using updated membership map
		for (const ztuple of left) {
			const keyTuple = rowKey(ztuple.tuple);
			const contentId = ztuple.tuple.contentId();
			if (newState.keySet.has(keyTuple)) {
				out = out.add(ztuple);
			}
			const rowSetByKey = newState.rowSetByKeys.get(contentId, ZSet.empty<Row>());
			newState.rowSetByKeys = newState.rowSetByKeys.set(
				keyTuple.contentId(),
				rowSetByKey.add(ztuple)
			);
		}

		return [out, newState];
	};
}

//
// Enriched Semijoin State.
//

export type SemijoinState<Row, Key> = {
	type: "semijoin";
	rowSetByKeys: Arrangement<Row>;
	keySet: ZSet<Key>;
};
export const emptySemijoinState = <Row, Key>(): SemijoinState<Row, Key> => ({
	type: "semijoin",
	rowSetByKeys: Map() satisfies Arrangement<Row>,
	keySet: ZSet.empty<Key>(),
});

export const hydrateSemijoinState = <Row, Key>(
	state: RawSemijoinState
): SemijoinState<Row, Key> => ({
	type: "semijoin",
	rowSetByKeys: hydrateArrangement(state.rowSetByKeys),
	keySet: hydrateZSet<Key>(state.keySet),
});

export const dehydrateSemijoinState = <Row, Key>(
	state: SemijoinState<Row, Key>
): RawSemijoinState => ({
	type: "semijoin",
	rowSetByKeys: dehydrateArrangement(state.rowSetByKeys),
	keySet: dehydrateZSet(state.keySet),
});

//
// Raw Semijoin State.
//

export const rawSemijoinStateSchema = z.object({
	type: z.literal("semijoin"),
	rowSetByKeys: rawArrangementSchema,
	keySet: rawZSetSchema,
});
export type RawSemijoinState = z.infer<typeof rawSemijoinStateSchema>;
export const emptyRawSemijoinState = (): RawSemijoinState => ({
	type: "semijoin",
	rowSetByKeys: {},
	keySet: { tuples: [] },
});

//
// Semijoin Spec.
//

export const semijoinSpecSchema = z.object({
	type: z.literal("semijoin"),
	rowKey: jsonpathRefSchema,
	right: subqueryRefSchema,
});
export type SemijoinSpec = z.infer<typeof semijoinSpecSchema>;

export const semijoinSpec = (rowKey: JsonPathRef, right: SubqueryRef): SemijoinSpec => ({
	type: "semijoin",
	rowKey,
	right,
});
